/**
 * @fileoverview The photo help's refusals (D23): what the server says when an AI request
 * is over the allowance, read off the failed request.
 *
 * A 429 with `error: 'AI_LIMIT'` and a `limit`:
 *   - 'month' — a subscriber's month reached, for one feature
 *     (AI_LIMIT_MONTH_PHOTO, _SCAN or _DOCTOR), with the day some comes back
 *     (`date`, already in the gardener's language and time zone);
 *   - 'trial' — a trial's smaller allowance used up (AI_LIMIT_TRIAL), answered
 *     with Subscribe;
 *   - 'day' — too many for one day (AI_LIMIT_DAY), the rest ready tomorrow.
 *
 * The burst limit (AI_LIMIT_BURST, "give it a minute") has no `limit` and is
 * not one of these: the screens say it as any other refusal.
 *
 * Where: read by the photo tray, the seed-packet scan and the plant
 * check-up when an AI request fails; AiLimitNotice shows the result.
 * API: none; it reads the 429 body of a request that already failed.
 * Words (en/es): the code becomes serverErrors.<code> in AiLimitNotice.
 * Watch out: a refusal with no `kind` is read as 'photo'.
 *
 * @module aiLimit
 */

import axios from 'axios';

/** Which AI feature stopped. */
export type AiLimitKind = 'photo' | 'scan' | 'doctor';

/** One of the photo help's refusals, as the screens show it (AiLimitNotice). */
export interface AiLimitRefusal {
  /** The server's code: AI_LIMIT_MONTH_PHOTO, AI_LIMIT_TRIAL, AI_LIMIT_DAY… (serverErrors.<code>). */
  code: string;
  /** Which limit: the month's, the trial's, or the day's. */
  limit: 'month' | 'trial' | 'day';
  /** Which feature reached it. */
  kind: AiLimitKind;
  /** The day some comes back, in the gardener's words ("October 12"); the month's only. */
  date: string | null;
}

/** The limits that are the allowance's (the burst limit is not). */
const LIMITS = new Set(['month', 'trial', 'day']);

/** The features a refusal may name. */
const KINDS = new Set(['photo', 'scan', 'doctor']);

/**
 * The photo help's refusal a request failed with, or null when it failed
 * for any other reason.
 *
 * @param error - Whatever the request threw.
 * @returns The refusal.
 */
export function aiLimitOf(error: unknown): AiLimitRefusal | null {
  if (!axios.isAxiosError(error) || error.response?.status !== 429) return null;
  const body = error.response.data as Record<string, unknown> | undefined;
  if (!body || body.error !== 'AI_LIMIT' || typeof body.limit !== 'string' || !LIMITS.has(body.limit)) return null;
  if (typeof body.code !== 'string') return null;
  return {
    code: body.code,
    limit: body.limit as AiLimitRefusal['limit'],
    kind: typeof body.kind === 'string' && KINDS.has(body.kind) ? body.kind as AiLimitKind : 'photo',
    date: typeof body.date === 'string' && body.date ? body.date : null,
  };
}
